/* eslint-disable max-lines-per-function, max-lines */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FeatureLayerServiceInfoDc, GeometryType, Permissions, QueryLayerServiceConfigurationDc } from "@evergis/api";
import { IconTypesKeys } from "@evergis/ui";

import { ADMIN_ROLE } from "../../../utils/constants";
import { isEditable } from "../../../evergis/core/permissions";
import { isObject } from "../../../utils/isObject";
import { getProject } from "../../../ducks/project/selectors";
import { getUsername } from "../../../ducks/user";
import { useTableModule } from "../../../hooks/appConfig/useAppModule";
import { useIsShared } from "../../../hooks/api/useIsShared";
import { useProjectInfo } from "../../../ducks/project/hooks";
import { useUser } from "../../../hooks/user/useUser";

import { TableInfo } from "../../../interfaces/resourceInfo";
import { LayerFetchStatus } from "../../../resources/layerWithTable";
import { ItemConfig, LayerItemMenuOption, MenuPermission } from "../components/ListMenu/types";

const getMenuPermission = (isError: boolean, permissions: Permissions): MenuPermission => {
  return isError ? "error" : isEditable(permissions) ? "write" : "read";
};

const showItem = (config: ItemConfig) => (item: LayerItemMenuOption) => !item.show || item.show(config);

const disableItem = (config: ItemConfig) => (item: LayerItemMenuOption) =>
  item.disable ? { ...item, disabled: item.disable(config) } : item;

export const useMenuOptions = (
  layer: FeatureLayerServiceInfoDc,
  table: TableInfo,
  status: LayerFetchStatus,
  loading?: boolean,
  isVisible?: boolean,
  hasRasters?: boolean,
  isGroup?: boolean,
): LayerItemMenuOption[] => {
  const isShared = useIsShared();
  const { t } = useTranslation("layerList");
  const { permissions: projectPermissions } = useSelector(getProject);
  const isTableOn = useTableModule() !== undefined;
  const layerPermission = getMenuPermission(status === "layerError" || !layer?.name, layer?.permissions);
  const tablePermission = getMenuPermission(status === "tableError" || !table?.name, table?.permissions);
  const ableToZoom = !loading && isVisible && !!(layer && (layer.type === "ProxyService" || layer.objectCount));
  const [{ permissions, name, owner }] = useProjectInfo();
  const username = useSelector(getUsername);
  const user = useUser();
  const isAdmin = user?.roles?.includes(ADMIN_ROLE);
  const isOwner = owner && owner === username && !!name;
  const isConfigurable = permissions?.includes(Permissions.Configure);

  const menuItems = useMemo(
    () =>
      [
        {
          text: t("actions.save", { ns: "common" }),
          value: "saveTempLayer",
          icon: "save",
          show: inputConfig => inputConfig.isTemp,
        },
        {
          text: t("uploadData", { ns: "catalog" }),
          value: "uploadData",
          icon: "upload",
          disable: inputConfig => !inputConfig.isVisible,
          show: inputConfig =>
            !inputConfig.isShared &&
            !hasRasters &&
            inputConfig.type === "PostgresTileLayerService" &&
            (inputConfig.layerPermission === "write" || isAdmin),
        },
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("style"),
              value: "style",
              icon: "paint",
              show: inputConfig =>
                !inputConfig.isShared &&
                ((inputConfig.type === "PostgresLayerService" && inputConfig.tablePermission !== "error") ||
                  inputConfig.type === "QueryLayerService") &&
                !(inputConfig.type === "PostgresLayerService" && inputConfig.geometryType === GeometryType.Unknown),
            }
          : null,
        {
          text: t("zoomToLayer"),
          value: "zoomTo",
          icon: "zoom_to",
          disable: inputConfig => !inputConfig.isTemp && !inputConfig.ableToZoom,
          show: inputConfig =>
            inputConfig.layerPermission !== "error" &&
            (inputConfig.type === "ProxyService" ||
              ["read", "write"].includes(inputConfig.layerPermission) ||
              isAdmin ||
              inputConfig.tablePermission !== "error") &&
            !(inputConfig.type === "PostgresLayerService" && inputConfig.geometryType === GeometryType.Unknown),
        },
        {
          text: t("addObject"),
          value: "addObject",
          icon: "feature_add",
          show: inputConfig =>
            !inputConfig.isShared &&
            (inputConfig.layerPermission === "write" || isAdmin) &&
            ((inputConfig.type === "PostgresLayerService" &&
              inputConfig.geometryType === GeometryType.Unknown &&
              inputConfig.tablePermission !== "error") ||
              inputConfig.type === "QueryLayerService"),
        },
        {
          text: t("showTable"),
          value: "openTable",
          icon: "geom_unknown",
          show: inputConfig =>
            inputConfig.layerPermission !== "error" &&
            (inputConfig.tablePermission !== "error" ||
              ["read", "write"].includes(inputConfig.layerPermission) ||
              isAdmin ||
              inputConfig.type === "QueryLayerService") &&
            inputConfig.isTableOn,
        },
        {
          text: t("showStats"),
          value: "openStats",
          icon: "stats",
          show: inputConfig =>
            (inputConfig.tablePermission !== "error" || inputConfig.type === "QueryLayerService") &&
            !inputConfig.isGroup &&
            !["RemoteTileService", "ProxyService"].includes(inputConfig.type),
        },
        {
          text: t("layerProperties"),
          value: "layerProperties",
          icon: "three_layers",
          show: inputConfig =>
            !inputConfig.isShared && !inputConfig.isGroup && (inputConfig.layerPermission === "write" || isAdmin),
        },
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("layerFilters"),
              value: "layerFilter",
              icon: "layer_filter",
              show: inputConfig =>
                !inputConfig.isTemp &&
                !inputConfig.isShared &&
                inputConfig.type === "QueryLayerService" &&
                inputConfig.hasEqlParams,
            }
          : null,
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("scaleRestriction"),
              value: "scaleRestriction",
              icon: "minimize",
              show: inputConfig =>
                !inputConfig.isTemp &&
                !inputConfig.isShared &&
                (["ProxyService", "QueryLayerService"].includes(inputConfig.type) ||
                  inputConfig.tablePermission !== "error") &&
                !(inputConfig.type === "PostgresLayerService" && inputConfig.geometryType === GeometryType.Unknown),
            }
          : null,
        {
          text: t("copyLayer"),
          value: "layerCopy",
          icon: "copy",
          show: inputConfig =>
            !inputConfig.isTemp &&
            !inputConfig.isShared &&
            inputConfig.layerPermission !== "error" &&
            (inputConfig.type === "ProxyService" ||
              ["read", "write"].includes(inputConfig.layerPermission) ||
              isAdmin ||
              inputConfig.tablePermission !== "error"),
        },
        {
          text: t("copyWmsLink"),
          value: "copyWms",
          icon: "wms_link",
          show: inputConfig =>
            !inputConfig.isTemp &&
            inputConfig.layerPermission !== "error" &&
            (inputConfig.tablePermission !== "error" ||
              ["read", "write"].includes(inputConfig.layerPermission) ||
              isAdmin) &&
            !(inputConfig.type === "PostgresLayerService" && inputConfig.geometryType === GeometryType.Unknown),
        },
        {
          text: t("layerExport"),
          value: "layerExport",
          icon: "download",
          show: inputConfig =>
            !inputConfig.isTemp &&
            !inputConfig.isShared &&
            inputConfig.layerPermission !== "error" &&
            (inputConfig.tablePermission !== "error" ||
              ["read", "write"].includes(inputConfig.layerPermission) ||
              isAdmin ||
              inputConfig.type === "QueryLayerService"),
        },
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("configurations"),
              value: "configurations",
              icon: "configurations",
              show: inputConfig =>
                !inputConfig.isTemp &&
                !inputConfig.isShared &&
                (inputConfig.layerPermission === "write" || isAdmin) &&
                (["ProxyService", "QueryLayerService", "RemoteTileService"].includes(inputConfig.type) ||
                  inputConfig.tablePermission !== "error"),
            }
          : null,
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("eqlQuery"),
              value: "createEqlQuery",
              icon: "query" as IconTypesKeys,
              show: inputConfig =>
                !inputConfig.isShared &&
                (inputConfig.layerPermission === "write" || isAdmin) &&
                inputConfig.type === "QueryLayerService",
            }
          : null,
        isOwner || isConfigurable || isAdmin
          ? {
              text: t("removeLayer"),
              value: "removeLayer",
              icon: "delete",
              show: inputConfig =>
                !inputConfig.isShared && !inputConfig.isGroup && (projectPermissions !== Permissions.Read || isAdmin),
            }
          : null,
        {
          text: t("editGroup"),
          value: "editGroup",
          icon: "edit",
          show: inputConfig =>
            !inputConfig.isShared && inputConfig.isGroup && (projectPermissions !== Permissions.Read || isAdmin),
        },
        {
          text: t("addGroup"),
          value: "addGroup",
          icon: "add_group",
          show: inputConfig =>
            !inputConfig.isShared && inputConfig.isGroup && (projectPermissions !== Permissions.Read || isAdmin),
        },
        {
          text: t("unGroup"),
          value: "unGroup",
          icon: "ungroup",
          show: inputConfig =>
            !inputConfig.isShared && inputConfig.isGroup && (projectPermissions !== Permissions.Read || isAdmin),
        },
        {
          text: t("removeGroup"),
          value: "removeGroup",
          icon: "delete",
          show: inputConfig =>
            !inputConfig.isShared && inputConfig.isGroup && (projectPermissions !== Permissions.Read || isAdmin),
        },
      ].filter(Boolean) as LayerItemMenuOption[],
    [t, isOwner, isConfigurable, isAdmin, hasRasters, projectPermissions],
  );

  const config = useMemo(
    () => ({
      type: layer?.type,
      layerPermission,
      tablePermission,
      ableToZoom,
      isVisible,
      isTableOn,
      isGroup,
      isShared,
      geometryType: layer?.geometryType,
      hasEqlParams:
        isObject((layer?.configuration as QueryLayerServiceConfigurationDc)?.eqlParameters) &&
        !!Object.keys((layer?.configuration as QueryLayerServiceConfigurationDc)?.eqlParameters).length,
      isTemp: layer?.name?.startsWith("temp"),
    }),
    [
      layer?.type,
      layer?.geometryType,
      layer?.configuration,
      layer?.name,
      layerPermission,
      tablePermission,
      ableToZoom,
      isVisible,
      isTableOn,
      isGroup,
      isShared,
    ],
  );

  return useMemo(() => menuItems.filter(showItem(config)).map(disableItem(config)), [config, menuItems]);
};
